import React, { useState, useEffect, useMemo, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import axios from 'axios'
import { toast } from 'react-toastify'
import { PulseLoader } from 'react-spinners'
import { SALES_APPROVAL_PERMISSION } from '../Components/Permission'

const API_URL = process.env.REACT_APP_API_URL

function readPermissionsFromStorage() {
    try {
        const parsed = JSON.parse(localStorage.getItem('permissions') || '[]')
        return Array.isArray(parsed) ? parsed : []
    } catch {
        return []
    }
}

function parseApiResponse(raw) {
    let d = raw
    if (typeof d === 'string') {
        try {
            d = JSON.parse(d)
        } catch {
            return raw
        }
    }
    if (d && d.body != null) {
        if (typeof d.body === 'string') {
            try {
                return JSON.parse(d.body)
            } catch {
                return d.body
            }
        }
        return d.body
    }
    return d
}

function parseJsonIfString(value) {
    if (typeof value !== 'string') return value
    try {
        return JSON.parse(value)
    } catch {
        return value
    }
}

function normalizeApiPayload(raw) {
    let obj = raw
    if (!obj || typeof obj !== 'object') return raw
    obj = { ...obj }
    if (typeof obj.data === 'string') {
        obj.data = parseJsonIfString(obj.data)
    }
    if (obj.data && typeof obj.data === 'object' && typeof obj.data.data === 'string') {
        obj.data = { ...obj.data, ...parseJsonIfString(obj.data.data) }
    }
    return obj
}

function isApiSuccess(responseData) {
    if (!responseData || typeof responseData !== 'object') return false
    const s = responseData.success
    return s === true || s === 'true' || s === 1 || s === '1'
}

function extractCustomersPayload(responseData) {
    const root = responseData
    let dataBlock = root?.data != null ? root.data : root

    if (typeof dataBlock === 'string') {
        dataBlock = parseJsonIfString(dataBlock)
    }
    if (!dataBlock || typeof dataBlock !== 'object') {
        return { customers: [], pagination: {} }
    }

    if (Array.isArray(dataBlock)) {
        return {
            customers: dataBlock,
            pagination: root.pagination && typeof root.pagination === 'object' ? root.pagination : {}
        }
    }

    let list =
        dataBlock.customers ||
        dataBlock.customerList ||
        dataBlock.items ||
        root.customers

    if (list == null && dataBlock.data != null) {
        const inner =
            typeof dataBlock.data === 'string'
                ? parseJsonIfString(dataBlock.data)
                : dataBlock.data
        if (inner && typeof inner === 'object') {
            list = inner.customers || inner.customerList || inner.items
        }
    }

    if (
        (list == null || (Array.isArray(list) && list.length === 0)) &&
        dataBlock.customers == null &&
        dataBlock.customerList == null &&
        dataBlock.items == null &&
        (dataBlock._id != null || dataBlock.customerName != null)
    ) {
        list = [dataBlock]
    }

    const pagination =
        (dataBlock && dataBlock.pagination) || root.pagination || {}

    if (typeof list === 'string') {
        list = parseJsonIfString(list)
    }

    return {
        customers: Array.isArray(list) ? list : [],
        pagination: pagination && typeof pagination === 'object' && !Array.isArray(pagination)
            ? pagination
            : {}
    }
}

const PRIORITIES = ['High', 'Medium', 'Low']
const OPPORTUNITY_STAGES = [
    'Contacts',
    'Leads',
    'Opportunity',
    'Prospect',
    'Proposal In Progress',
    'Proposal Submission',
    'Negotiation in Progress',
    'Contract Won',
    'Lost',
    'Hold'
]

const SERVICE_OFFERINGS = [
    'Procure to Pay',
    'e-Sourcing',
    'Procurement Services',
    'Tail Spend Management',
    'SPOT Buys',
    'Inventory Management',
    'Stores Management',
    'Non Moving inventory Liquidation',
    'Vendor Managed Inventory',
    'Master data Management'
]

const SalesTrackerAdd = () => {
    const navigate = useNavigate()
    const canShowReviewerRemark = readPermissionsFromStorage().includes(
        SALES_APPROVAL_PERMISSION
    )
    const [customers, setCustomers] = useState([])
    const [filteredCustomers, setFilteredCustomers] = useState([])
    const [customerSearchTerm, setCustomerSearchTerm] = useState('')
    const [showCustomerDropdown, setShowCustomerDropdown] = useState(false)
    const [selectedCustomerDropdownIndex, setSelectedCustomerDropdownIndex] = useState(-1)
    const [loadingCustomers, setLoadingCustomers] = useState(true)
    const [selectedCustomerId, setSelectedCustomerId] = useState('')
    const [selectedContactKey, setSelectedContactKey] = useState('')

    const [companyName, setCompanyName] = useState('')
    const [contactId, setContactId] = useState('')
    const [contactPerson, setContactPerson] = useState('')
    const [designation, setDesignation] = useState('')
    const [contactNumber, setContactNumber] = useState('')
    const [email, setEmail] = useState('')
    const [city, setCity] = useState('')
    const [state, setState] = useState('')
    const [priority, setPriority] = useState('')
    const [opportunityStage, setOpportunityStage] = useState('')
    const [selectedServiceOfferings, setSelectedServiceOfferings] = useState([])
    const [serviceOfferingSearchTerm, setServiceOfferingSearchTerm] = useState('')
    const [showServiceOfferingDropdown, setShowServiceOfferingDropdown] = useState(false)
    const [selectedServiceOfferingIndex, setSelectedServiceOfferingIndex] = useState(-1)
    const [filteredServiceOfferings, setFilteredServiceOfferings] = useState(() => [
        ...SERVICE_OFFERINGS
    ])
    const [salesRemarkContent, setSalesRemarkContent] = useState('')
    const [reviewerRemarkContent, setReviewerRemarkContent] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const serviceOfferingDropdownRef = useRef(null)

    useEffect(() => {
        if (!showServiceOfferingDropdown) return undefined
        const handleOutsideClick = (event) => {
            if (
                serviceOfferingDropdownRef.current &&
                !serviceOfferingDropdownRef.current.contains(event.target)
            ) {
                setShowServiceOfferingDropdown(false)
                setSelectedServiceOfferingIndex(-1)
            }
        }
        document.addEventListener('mousedown', handleOutsideClick)
        return () => document.removeEventListener('mousedown', handleOutsideClick)
    }, [showServiceOfferingDropdown])

    useEffect(() => {
        const fetchCustomers = async () => {
            setLoadingCustomers(true)
            try {
                const payload = {
                    action: 'get-all-customers',
                    page: 1,
                    limit: 500,
                    search: ''
                }
                const response = await axios.post(API_URL, payload)
                const responseData = normalizeApiPayload(parseApiResponse(response.data))
                const { customers: list } = extractCustomersPayload(responseData)
                const ok =
                    isApiSuccess(responseData) ||
                    (Array.isArray(list) && list.length > 0)
                if (ok) {
                    setCustomers(list)
                }
            } catch (error) {
                console.error('Fetch customers for sales:', error)
            } finally {
                setLoadingCustomers(false)
            }
        }
        fetchCustomers()
    }, [])

    useEffect(() => {
        setFilteredCustomers(customers)
    }, [customers])

    const selectedCustomer = customers.find(
        (c) => String(c._id || c.id) === String(selectedCustomerId)
    )

    const contactOptions = useMemo(() => {
        if (!selectedCustomer || !Array.isArray(selectedCustomer.contacts)) return []
        return selectedCustomer.contacts.map((ct, idx) => ({
            key: ct._id != null ? String(ct._id) : `idx-${idx}`,
            contact: ct,
            label: `${ct.contactPerson || 'Contact'}${ct.email ? ` — ${ct.email}` : ''}`
        }))
    }, [selectedCustomer])

    /** When a contact is chosen from the list, show details as read-only (not editable). */
    const contactDetailsLocked =
        contactOptions.length > 0 && selectedContactKey !== ''

    const lockedInputClass = contactDetailsLocked
        ? 'form-control bg-light text-muted'
        : 'form-control'

    const applyContactFields = (ct) => {
        if (!ct) return
        setContactPerson(ct.contactPerson || '')
        setDesignation(ct.designation || '')
        setContactNumber((ct.contactNumber || '').replace(/\D/g, '').slice(0, 10))
        setEmail(ct.email || '')
        setCity(ct.city || '')
        setState(ct.state || '')
    }

    const clearContactFields = () => {
        setContactPerson('')
        setDesignation('')
        setContactNumber('')
        setEmail('')
        setCity('')
        setState('')
    }

    const handleCustomerSearchChange = (e) => {
        const searchValue = e.target.value
        setCustomerSearchTerm(searchValue)
        setShowCustomerDropdown(true)
        setSelectedCustomerDropdownIndex(-1)

        if (searchValue.trim() === '') {
            setFilteredCustomers(customers)
        } else {
            const q = searchValue.toLowerCase()
            const filtered = customers.filter((c) =>
                String(c.customerName || '')
                    .toLowerCase()
                    .includes(q)
            )
            setFilteredCustomers(filtered)
        }
    }

    const handleCustomerSelect = (customer) => {
        const id = String(customer._id || customer.id || '')
        const name = customer.customerName || ''
        if (!id) return

        setCustomerSearchTerm('')
        setShowCustomerDropdown(false)
        setSelectedCustomerDropdownIndex(-1)

        setSelectedCustomerId(id)
        setCompanyName(name)
        setContactId(id)
        setSelectedContactKey('')
        clearContactFields()
    }

    const handleRemoveSelectedCustomer = () => {
        setSelectedCustomerId('')
        setCompanyName('')
        setContactId('')
        setSelectedContactKey('')
        clearContactFields()
    }

    const handleCustomerKeyDown = (e) => {
        if (!showCustomerDropdown || filteredCustomers.length === 0) return

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault()
                setSelectedCustomerDropdownIndex((prev) =>
                    prev < filteredCustomers.length - 1 ? prev + 1 : prev
                )
                break
            case 'ArrowUp':
                e.preventDefault()
                setSelectedCustomerDropdownIndex((prev) => (prev > 0 ? prev - 1 : -1))
                break
            case 'Enter':
                e.preventDefault()
                if (
                    selectedCustomerDropdownIndex >= 0 &&
                    selectedCustomerDropdownIndex < filteredCustomers.length
                ) {
                    handleCustomerSelect(filteredCustomers[selectedCustomerDropdownIndex])
                } else if (filteredCustomers.length === 1) {
                    handleCustomerSelect(filteredCustomers[0])
                }
                break
            case 'Escape':
                setShowCustomerDropdown(false)
                setSelectedCustomerDropdownIndex(-1)
                break
            default:
                break
        }
    }

    const handleContactSelectChange = (e) => {
        const key = e.target.value
        setSelectedContactKey(key)
        if (!key) {
            clearContactFields()
            return
        }
        const opt = contactOptions.find((o) => o.key === key)
        if (opt?.contact) applyContactFields(opt.contact)
    }

    const handlePhoneChange = (e) => {
        const value = e.target.value.replace(/\D/g, '').slice(0, 10)
        setContactNumber(value)
    }

    const handleCityChange = (e) => {
        setCity(e.target.value.replace(new RegExp('[^a-zA-Z\\s,/-]', 'g'), ''))
    }

    const handleStateChange = (e) => {
        setState(e.target.value.replace(/[^a-zA-Z\s]/g, ''))
    }

    const handleServiceOfferingSearchChange = (e) => {
        const searchValue = e.target.value
        setServiceOfferingSearchTerm(searchValue)
        setShowServiceOfferingDropdown(true)
        setSelectedServiceOfferingIndex(-1)

        if (searchValue.trim() === '') {
            setFilteredServiceOfferings([...SERVICE_OFFERINGS])
        } else {
            const q = searchValue.toLowerCase()
            setFilteredServiceOfferings(
                SERVICE_OFFERINGS.filter((s) => s.toLowerCase().includes(q))
            )
        }
    }

    const handleServiceOfferingToggle = (name) => {
        const n = String(name || '').trim()
        if (!n) return

        setSelectedServiceOfferings((prev) => {
            if (prev.includes(n)) return prev.filter((x) => x !== n)
            return [...prev, n]
        })
    }

    const handleServiceOfferingSelectAllVisible = () => {
        setSelectedServiceOfferings((prev) => {
            const next = new Set(prev)
            filteredServiceOfferings.forEach((item) => next.add(item))
            return Array.from(next)
        })
    }

    const handleServiceOfferingClearAll = () => {
        setSelectedServiceOfferings([])
    }

    const handleServiceOfferingKeyDown = (e) => {
        if (!showServiceOfferingDropdown || filteredServiceOfferings.length === 0) return

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault()
                setSelectedServiceOfferingIndex((prev) =>
                    prev < filteredServiceOfferings.length - 1 ? prev + 1 : prev
                )
                break
            case 'ArrowUp':
                e.preventDefault()
                setSelectedServiceOfferingIndex((prev) => (prev > 0 ? prev - 1 : -1))
                break
            case 'Enter':
            case ' ':
                e.preventDefault()
                if (
                    selectedServiceOfferingIndex >= 0 &&
                    selectedServiceOfferingIndex < filteredServiceOfferings.length
                ) {
                    handleServiceOfferingToggle(
                        filteredServiceOfferings[selectedServiceOfferingIndex]
                    )
                }
                break
            case 'Escape':
                setShowServiceOfferingDropdown(false)
                setSelectedServiceOfferingIndex(-1)
                break
            default:
                break
        }
    }

    /** Re-open with full catalog when search is empty (fixes stale narrow list after a pick). */
    const handleServiceOfferingFocus = () => {
        setShowServiceOfferingDropdown(true)
        setSelectedServiceOfferingIndex(-1)
        const term = serviceOfferingSearchTerm.trim()
        if (term === '') {
            setFilteredServiceOfferings([...SERVICE_OFFERINGS])
        } else {
            const q = term.toLowerCase()
            setFilteredServiceOfferings(
                SERVICE_OFFERINGS.filter((s) => s.toLowerCase().includes(q))
            )
        }
    }

    const validateForm = () => {
        if (!selectedCustomerId) {
            toast.error('Please select a customer', { autoClose: 2000 })
            return false
        }
        if (contactOptions.length > 0 && !selectedContactKey) {
            toast.error('Please select a contact', { autoClose: 2000 })
            return false
        }
        if (!priority) {
            toast.error('Please select priority', { autoClose: 2000 })
            return false
        }
        if (!opportunityStage) {
            toast.error('Please select opportunity stage', { autoClose: 2000 })
            return false
        }
        if (!companyName.trim()) {
            toast.error('Company name is required', { autoClose: 2000 })
            return false
        }
        if (!contactId.trim()) {
            toast.error('Customer reference is missing', { autoClose: 2000 })
            return false
        }
        if (!contactPerson.trim()) {
            toast.error('Contact person is required', { autoClose: 2000 })
            return false
        }
        if (!email.trim()) {
            toast.error('Email is required', { autoClose: 2000 })
            return false
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
            toast.error('Please enter a valid email', { autoClose: 2000 })
            return false
        }
        if (!contactNumber.trim() || contactNumber.length !== 10) {
            toast.error('Contact number must be 10 digits', { autoClose: 2000 })
            return false
        }
        if (!city.trim()) {
            toast.error('City is required', { autoClose: 2000 })
            return false
        }
        if (!state.trim()) {
            toast.error('State is required', { autoClose: 2000 })
            return false
        }
        if (selectedServiceOfferings.length === 0) {
            toast.error('Please select at least one service offering', { autoClose: 2000 })
            return false
        }
        if (!salesRemarkContent.trim()) {
            toast.error('Sales remark is required', { autoClose: 2000 })
            return false
        }
        return true
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        if (!validateForm()) return

        const requestUserId = localStorage.getItem('id')
        const userName = localStorage.getItem('name') || ''
        if (!requestUserId) {
            toast.error('User session expired. Please login again.', { autoClose: 2000 })
            setTimeout(() => navigate('/'), 2000)
            return
        }

        setIsLoading(true)
        try {
            const payload = {
                action: 'create-sales',
                requestUserId,
                companyName: companyName.trim(),
                contactId: contactId.trim(),
                contactPerson: contactPerson.trim(),
                designation: designation.trim(),
                contactNumber: contactNumber.trim(),
                email: email.trim(),
                city: city.trim(),
                state: state.trim(),
                priority,
                opportunityStage,
                serviceOffering: selectedServiceOfferings,
                salesRemark: {
                    content: salesRemarkContent.trim(),
                    userId: requestUserId,
                    userName: userName.trim()
                }
            }

            const reviewerText = reviewerRemarkContent.trim()
            if (canShowReviewerRemark && reviewerText) {
                payload.reviewerRemark = {
                    content: reviewerText,
                    userId: requestUserId,
                    userName: userName.trim()
                }
            }

            const response = await axios.post(API_URL, payload)
            const responseData = normalizeApiPayload(parseApiResponse(response.data))

            if (isApiSuccess(responseData)) {
                toast.success(responseData.message || 'Sales record created successfully', {
                    autoClose: 2000
                })
                navigate('/salesTracker', { replace: true })
            } else {
                toast.error(responseData?.message || 'Failed to create sales record', { autoClose: 2000 })
            }
        } catch (error) {
            console.error('Create sales error:', error)
            const errData = error.response?.data
                ? parseApiResponse(error.response.data)
                : null
            const msg =
                typeof errData === 'object' && errData?.message
                    ? errData.message
                    : error?.message || 'An error occurred while creating sales record'
            toast.error(msg, { autoClose: 2000 })
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div>
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2 align-items-center">
                        <div className="col-6 col-sm-6">
                            <h1 className="m-0 txt_lft">Create Sales</h1>
                        </div>
                        <div className="col-6 col-sm-6">
                            <ol className="breadcrumb justify-content-end">
                                <li className="breadcrumb-item">
                                    <Link to="/salesTracker">
                                        <i className="fa-solid fa-arrow-left-long" /> Back
                                    </Link>
                                </li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>
            <div className="content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="frmwdth_cntr">
                            <div className="col-lg-12">
                                <div className="card card-primary card-outline">
                                    <form className="form-horizontal" onSubmit={handleSubmit}>
                                        <div className="card-body">
                                            {loadingCustomers ? (
                                                <div className="text-center py-4">
                                                    <PulseLoader color="#1199B2" size={10} />

                                                </div>
                                            ) : (
                                                <>
                                                    <div className="form-group row">
                                                        <label className="col-12 col-md-4 col-form-label">
                                                            Customer <span className="text-danger">*</span>
                                                        </label>
                                                        <div className="col-12 col-md-8">
                                                            {selectedCustomerId && companyName && (
                                                                <div className="mb-2">
                                                                    <span
                                                                        className="badge badge-info mr-2 mb-1"
                                                                        style={{ fontSize: '12px', fontWeight: 550 }}
                                                                    >
                                                                        {companyName}
                                                                        <span
                                                                            className="ml-2 crsr_pntr"
                                                                            onMouseDown={(ev) => {
                                                                                ev.preventDefault()
                                                                                handleRemoveSelectedCustomer()
                                                                            }}
                                                                            title="Remove"
                                                                        >
                                                                            <i className="fa-solid fa-xmark"></i>
                                                                        </span>
                                                                    </span>
                                                                </div>
                                                            )}
                                                            <div style={{ position: 'relative' }}>
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    placeholder={
                                                                        loadingCustomers
                                                                            ? 'Loading customers...'
                                                                            : 'Type to search customer and press Enter...'
                                                                    }
                                                                    value={customerSearchTerm}
                                                                    onChange={handleCustomerSearchChange}
                                                                    onKeyDown={handleCustomerKeyDown}
                                                                    onFocus={() => setShowCustomerDropdown(true)}
                                                                    onBlur={() => {
                                                                        setTimeout(() => {
                                                                            setShowCustomerDropdown(false)
                                                                            setSelectedCustomerDropdownIndex(-1)
                                                                        }, 200)
                                                                    }}
                                                                    autoComplete="off"
                                                                    disabled={loadingCustomers}
                                                                />
                                                                {showCustomerDropdown &&
                                                                    !loadingCustomers &&
                                                                    filteredCustomers.length > 0 && (
                                                                        <div
                                                                            style={{
                                                                                position: 'absolute',
                                                                                top: '100%',
                                                                                left: 0,
                                                                                right: 0,
                                                                                maxHeight: '200px',
                                                                                overflowY: 'auto',
                                                                                backgroundColor: '#fff',
                                                                                border: '1px solid #ced4da',
                                                                                borderRadius: '4px',
                                                                                zIndex: 1000,
                                                                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                                                            }}
                                                                        >
                                                                            {filteredCustomers.map((c, index) => {
                                                                                const cid = String(c._id || c.id || '')
                                                                                return (
                                                                                    <div
                                                                                        key={cid || index}
                                                                                        onMouseDown={(ev) => {
                                                                                            ev.preventDefault()
                                                                                            handleCustomerSelect(c)
                                                                                        }}
                                                                                        style={{
                                                                                            padding: '8px 12px',
                                                                                            cursor: 'pointer',
                                                                                            borderBottom: '1px solid #f0f0f0',
                                                                                            backgroundColor:
                                                                                                index ===
                                                                                                selectedCustomerDropdownIndex
                                                                                                    ? '#e9ecef'
                                                                                                    : '#fff'
                                                                                        }}
                                                                                        onMouseEnter={() =>
                                                                                            setSelectedCustomerDropdownIndex(index)
                                                                                        }
                                                                                    >
                                                                                        {c.customerName || '—'}
                                                                                    </div>
                                                                                )
                                                                            })}
                                                                        </div>
                                                                    )}
                                                                {showCustomerDropdown &&
                                                                    !loadingCustomers &&
                                                                    customerSearchTerm &&
                                                                    filteredCustomers.length === 0 && (
                                                                        <div
                                                                            style={{
                                                                                position: 'absolute',
                                                                                top: '100%',
                                                                                left: 0,
                                                                                right: 0,
                                                                                backgroundColor: '#fff',
                                                                                border: '1px solid #ced4da',
                                                                                borderRadius: '4px',
                                                                                padding: '8px 12px',
                                                                                zIndex: 1000,
                                                                                color: '#6c757d',
                                                                                fontSize: '13px'
                                                                            }}
                                                                        >
                                                                            No customers found
                                                                        </div>
                                                                    )}
                                                            </div>
                                                            <small className="form-text text-muted">
                                                                Search and choose a customer to set company and contact options below.
                                                            </small>
                                                        </div>
                                                    </div>

                                                    {contactOptions.length > 0 && (
                                                        <div className="form-group row">
                                                            <label className="col-12 col-md-4 col-form-label">
                                                                Contact <span className="text-danger">*</span>
                                                            </label>
                                                            <div className="col-12 col-md-8">
                                                                <select
                                                                    className="form-control"
                                                                    value={selectedContactKey}
                                                                    onChange={handleContactSelectChange}
                                                                >
                                                                    <option value="">— Select contact —</option>
                                                                    {contactOptions.map((o) => (
                                                                        <option key={o.key} value={o.key}>
                                                                            {o.label}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                                {contactDetailsLocked && (
                                                                    <small className="text-muted d-block mt-1">
                                                                        Contact details below are taken from your selection and cannot be
                                                                        edited.
                                                                    </small>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}

                                                    <div className="form-group row">
                                                        <label className="col-12 col-md-4 col-form-label">Company Name</label>
                                                        <div className="col-12 col-md-8">
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                placeholder="Company name"
                                                                value={companyName}
                                                                onChange={(e) => setCompanyName(e.target.value)}
                                                                required
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="form-group row">
                                                        <label className="col-12 col-md-4 col-form-label">Contact Person</label>
                                                        <div className="col-12 col-md-8">
                                                            <input
                                                                type="text"
                                                                className={lockedInputClass}
                                                                placeholder="Contact person"
                                                                value={contactPerson}
                                                                onChange={(e) => setContactPerson(e.target.value)}
                                                                disabled={contactDetailsLocked}
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="form-group row">
                                                        <label className="col-12 col-md-4 col-form-label">Designation</label>
                                                        <div className="col-12 col-md-8">
                                                            <input
                                                                type="text"
                                                                className={lockedInputClass}
                                                                placeholder="Designation"
                                                                value={designation}
                                                                onChange={(e) => setDesignation(e.target.value)}
                                                                disabled={contactDetailsLocked}
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="form-group row">
                                                        <label className="col-12 col-md-4 col-form-label">Email</label>
                                                        <div className="col-12 col-md-8">
                                                            <input
                                                                type="email"
                                                                className={lockedInputClass}
                                                                placeholder="Email"
                                                                value={email}
                                                                onChange={(e) => setEmail(e.target.value)}
                                                                disabled={contactDetailsLocked}
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="form-group row">
                                                        <label className="col-12 col-md-4 col-form-label">Phone</label>
                                                        <div className="col-12 col-md-8">
                                                            <input
                                                                type="tel"
                                                                className={lockedInputClass}
                                                                placeholder="10-digit mobile"
                                                                value={contactNumber}
                                                                onChange={handlePhoneChange}
                                                                onKeyPress={(e) => {
                                                                    if (
                                                                        !/[0-9]/.test(e.key) &&
                                                                        e.key !== 'Backspace' &&
                                                                        e.key !== 'Delete' &&
                                                                        e.key !== 'Tab'
                                                                    ) {
                                                                        e.preventDefault()
                                                                    }
                                                                }}
                                                                maxLength={10}
                                                                inputMode="numeric"
                                                                disabled={contactDetailsLocked}
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="form-group row">
                                                        <label className="col-12 col-md-4 col-form-label">City</label>
                                                        <div className="col-12 col-md-8">
                                                            <input
                                                                type="text"
                                                                className={lockedInputClass}
                                                                placeholder="City"
                                                                value={city}
                                                                onChange={handleCityChange}
                                                                disabled={contactDetailsLocked}
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="form-group row">
                                                        <label className="col-12 col-md-4 col-form-label">State</label>
                                                        <div className="col-12 col-md-8">
                                                            <input
                                                                type="text"
                                                                className={lockedInputClass}
                                                                placeholder="State"
                                                                value={state}
                                                                onChange={handleStateChange}
                                                                disabled={contactDetailsLocked}
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="form-group row">
                                                        <label className="col-12 col-md-4 col-form-label">
                                                            Priority <span className="text-danger">*</span>
                                                        </label>
                                                        <div className="col-12 col-md-8">
                                                            <select
                                                                className="form-control"
                                                                value={priority}
                                                                onChange={(e) => setPriority(e.target.value)}
                                                            >
                                                                <option value="">Select priority</option>
                                                                {PRIORITIES.map((p) => (
                                                                    <option key={p} value={p}>
                                                                        {p}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    </div>

                                                    <div className="form-group row">
                                                        <label className="col-12 col-md-4 col-form-label">
                                                            Opportunity Stage <span className="text-danger">*</span>
                                                        </label>
                                                        <div className="col-12 col-md-8">
                                                            <select
                                                                className="form-control"
                                                                value={opportunityStage}
                                                                onChange={(e) => setOpportunityStage(e.target.value)}
                                                            >
                                                                <option value="">Select opportunity stage</option>
                                                                {OPPORTUNITY_STAGES.map((s) => (
                                                                    <option key={s} value={s}>
                                                                        {s}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    </div>

                                                    <div className="form-group row">
                                                        <label className="col-12 col-md-4 col-form-label">
                                                            Service Offering{' '}
                                                            <span className="text-danger">*</span>
                                                        </label>
                                                        <div className="col-12 col-md-8">
                                                            {selectedServiceOfferings.length > 0 && (
                                                                <div className="mb-2">
                                                                    {selectedServiceOfferings.map((label) => (
                                                                        <span
                                                                            key={label}
                                                                            className="badge badge-info mr-2 mb-1"
                                                                            style={{
                                                                                fontSize: '12px',
                                                                                fontWeight: 550
                                                                            }}
                                                                        >
                                                                            {label}
                                                                            <span
                                                                                className="ml-2 crsr_pntr"
                                                                                onMouseDown={(ev) => {
                                                                                    ev.preventDefault()
                                                                                    handleServiceOfferingToggle(label)
                                                                                }}
                                                                                title="Remove"
                                                                            >
                                                                                <i className="fa-solid fa-xmark" />
                                                                            </span>
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            )}
                                                            <div
                                                                ref={serviceOfferingDropdownRef}
                                                                style={{ position: 'relative' }}
                                                            >
                                                                <button
                                                                    type="button"
                                                                    className="form-control text-left d-flex align-items-center justify-content-between"
                                                                    onClick={() => {
                                                                        if (showServiceOfferingDropdown) {
                                                                            setShowServiceOfferingDropdown(false)
                                                                            setSelectedServiceOfferingIndex(-1)
                                                                        } else {
                                                                            handleServiceOfferingFocus()
                                                                        }
                                                                    }}
                                                                    style={{
                                                                        height: 'auto',
                                                                        minHeight: '38px',
                                                                        whiteSpace: 'normal'
                                                                    }}
                                                                >
                                                                    <span
                                                                        style={{
                                                                            color:
                                                                                selectedServiceOfferings.length > 0
                                                                                    ? '#212529'
                                                                                    : '#6c757d'
                                                                        }}
                                                                    >
                                                                        {selectedServiceOfferings.length > 0
                                                                            ? `${selectedServiceOfferings.length} selected`
                                                                            : 'Select service offerings'}
                                                                    </span>
                                                                    <i
                                                                        className={`fa-solid ${
                                                                            showServiceOfferingDropdown
                                                                                ? 'fa-chevron-up'
                                                                                : 'fa-chevron-down'
                                                                        }`}
                                                                        style={{ fontSize: '12px', color: '#6c757d' }}
                                                                    />
                                                                </button>

                                                                {showServiceOfferingDropdown && (
                                                                    <div
                                                                        style={{
                                                                            position: 'absolute',
                                                                            bottom: '100%',
                                                                            left: 0,
                                                                            right: 0,
                                                                            marginBottom: '4px',
                                                                            backgroundColor: '#fff',
                                                                            border: '1px solid #ced4da',
                                                                            borderRadius: '4px',
                                                                            zIndex: 1050,
                                                                            boxShadow: '0 -4px 12px rgba(0,0,0,0.12)'
                                                                        }}
                                                                        onMouseDown={(ev) => ev.preventDefault()}
                                                                    >
                                                                        <div
                                                                            style={{
                                                                                padding: '8px',
                                                                                borderBottom: '1px solid #e9ecef'
                                                                            }}
                                                                        >
                                                                            <input
                                                                                type="text"
                                                                                className="form-control form-control-sm"
                                                                                placeholder="Search service offerings..."
                                                                                value={serviceOfferingSearchTerm}
                                                                                onChange={handleServiceOfferingSearchChange}
                                                                                onKeyDown={handleServiceOfferingKeyDown}
                                                                                onFocus={handleServiceOfferingFocus}
                                                                                autoComplete="off"
                                                                                autoFocus
                                                                            />
                                                                            <div className="d-flex justify-content-between align-items-center mt-2">
                                                                                <small className="text-muted">
                                                                                    {selectedServiceOfferings.length} selected
                                                                                </small>
                                                                                <div>
                                                                                    <button
                                                                                        type="button"
                                                                                        className="btn btn-link btn-sm p-0 mr-3"
                                                                                        onClick={handleServiceOfferingSelectAllVisible}
                                                                                    >
                                                                                        Select all
                                                                                    </button>
                                                                                    <button
                                                                                        type="button"
                                                                                        className="btn btn-link btn-sm p-0 text-danger"
                                                                                        onClick={handleServiceOfferingClearAll}
                                                                                        disabled={
                                                                                            selectedServiceOfferings.length === 0
                                                                                        }
                                                                                    >
                                                                                        Clear
                                                                                    </button>
                                                                                </div>
                                                                            </div>
                                                                        </div>

                                                                        <div
                                                                            style={{
                                                                                maxHeight: '220px',
                                                                                overflowY: 'auto'
                                                                            }}
                                                                        >
                                                                            {filteredServiceOfferings.length > 0 ? (
                                                                                filteredServiceOfferings.map(
                                                                                    (offering, index) => {
                                                                                        const checked =
                                                                                            selectedServiceOfferings.includes(
                                                                                                offering
                                                                                            )
                                                                                        const isHighlighted =
                                                                                            index ===
                                                                                            selectedServiceOfferingIndex
                                                                                        const rowBg = isHighlighted
                                                                                            ? '#e9ecef'
                                                                                            : checked
                                                                                              ? '#f0f9fb'
                                                                                              : '#fff'
                                                                                        return (
                                                                                            <div
                                                                                                key={offering}
                                                                                                className="d-flex align-items-center"
                                                                                                style={{
                                                                                                    padding: '8px 12px',
                                                                                                    cursor: 'pointer',
                                                                                                    borderBottom:
                                                                                                        '1px solid #f0f0f0',
                                                                                                    backgroundColor: rowBg,
                                                                                                    fontWeight: 'normal'
                                                                                                }}
                                                                                                onClick={() =>
                                                                                                    handleServiceOfferingToggle(
                                                                                                        offering
                                                                                                    )
                                                                                                }
                                                                                                onMouseEnter={() =>
                                                                                                    setSelectedServiceOfferingIndex(
                                                                                                        index
                                                                                                    )
                                                                                                }
                                                                                            >
                                                                                                <input
                                                                                                    type="checkbox"
                                                                                                    className="mr-2"
                                                                                                    checked={checked}
                                                                                                    onChange={() =>
                                                                                                        handleServiceOfferingToggle(
                                                                                                            offering
                                                                                                        )
                                                                                                    }
                                                                                                    onClick={(e) =>
                                                                                                        e.stopPropagation()
                                                                                                    }
                                                                                                />
                                                                                                <span
                                                                                                    style={{
                                                                                                        fontSize: '14px',
                                                                                                        fontWeight: 400
                                                                                                    }}
                                                                                                >
                                                                                                    {offering}
                                                                                                </span>
                                                                                            </div>
                                                                                        )
                                                                                    }
                                                                                )
                                                                            ) : (
                                                                                <div
                                                                                    style={{
                                                                                        padding: '10px 12px',
                                                                                        color: '#6c757d',
                                                                                        fontSize: '13px'
                                                                                    }}
                                                                                >
                                                                                    No service offerings found
                                                                                </div>
                                                                            )}
                                                                        </div>

                                                                        <div
                                                                            className="text-right"
                                                                            style={{
                                                                                padding: '8px 12px',
                                                                                borderTop: '1px solid #e9ecef',
                                                                                backgroundColor: '#f8f9fa'
                                                                            }}
                                                                        >
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-sm btn-blue"
                                                                                onClick={() => {
                                                                                    setShowServiceOfferingDropdown(false)
                                                                                    setSelectedServiceOfferingIndex(-1)
                                                                                }}
                                                                            >
                                                                                Done
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="form-group row">
                                                        <label className="col-12 col-md-4 col-form-label">Sales remark</label>
                                                        <div className="col-12 col-md-8">
                                                            <textarea
                                                                className="form-control"
                                                                rows={3}
                                                                placeholder="Initial sales note"
                                                                value={salesRemarkContent}
                                                                onChange={(e) => setSalesRemarkContent(e.target.value)}
                                                                required
                                                            />
                                                        </div>
                                                    </div>

                                                    {canShowReviewerRemark && (
                                                        <div className="form-group row">
                                                            <label className="col-12 col-md-4 col-form-label">
                                                                Reviewer remark{' '}
                                                                <small className="text-muted font-weight-normal">
                                                                    (optional)
                                                                </small>
                                                            </label>
                                                            <div className="col-12 col-md-8">
                                                                <textarea
                                                                    className="form-control"
                                                                    rows={3}
                                                                    placeholder="Initial reviewer note (optional)"
                                                                    value={reviewerRemarkContent}
                                                                    onChange={(e) =>
                                                                        setReviewerRemarkContent(e.target.value)
                                                                    }
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>

                                        <div className="card-footer text-center">
                                            <button
                                                type="submit"
                                                className="btn btn-blue"
                                                disabled={isLoading || loadingCustomers}
                                            >
                                                {isLoading ? 'Creating...' : 'Create'}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}


export default SalesTrackerAdd
